import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axiosInstance from '../../api/axios';
import Pagination from '../../components/Pagination';
import SortableTableHeader from '../../components/SortableTableHeader';

const CREDIT_TYPES = ['image', 'video', 'chat', 'web_search', 'ipvpn', 'signalX'];

const defaultFilters = { page: 1, pageSize: 15, type: '', username: '', startDate: '', endDate: '', order: '' };

const inputCls =
  "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";

const CreditsUsageHistory = () => {
  const { token } = useSelector((state) => state.auth);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(defaultFilters);
  const [tempFilters, setTempFilters] = useState(defaultFilters);
  const [sort, setSort] = useState({ key: '', direction: 'asc' });
  const [sortedData, setSortedData] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRows: 0 });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: filters.page, pageSize: filters.pageSize };
      if (filters.type) params.type = filters.type;
      if (filters.username) params.username = filters.username;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.order) params.order = filters.order;

      const response = await axiosInstance.get('/api/admin/dashboard/app-credit-usage', {
        headers: { token },
        params,
      });

      if (response.data.success) {
        const newData = response.data.data.data;
        setData(newData);
        setSortedData(newData);
        setPagination({
          currentPage: parseInt(response.data.data.currentPage) || 1,
          totalPages: response.data.data.totalPages || 1,
          totalRows: response.data.data.totalRows || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching credits usage history:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!data.length) return;
    const sorted = [...data].sort((a, b) => {
      if (!sort.key) return 0;
      let aVal = a[sort.key] ?? '';
      let bVal = b[sort.key] ?? '';
      if (sort.key === 'amount') {
        return sort.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (sort.key === 'createdAt' || sort.key === 'updatedAt') {
        return sort.direction === 'asc'
          ? new Date(aVal) - new Date(bVal)
          : new Date(bVal) - new Date(aVal);
      }
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      return sort.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
    setSortedData(sorted);
  }, [data, sort]);

  const handleTempFilterChange = (key, value) => {
    setTempFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => setFilters({ ...tempFilters, page: 1 });

  const clearFilters = () => {
    setFilters(defaultFilters);
    setTempFilters(defaultFilters);
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
    setTempFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const formatDate = (d) => new Date(d).toLocaleString();

  const exportToCSV = async () => {
    try {
      const params = { page: 1, pageSize: 10000 };
      if (filters.type) params.type = filters.type;
      if (filters.username) params.username = filters.username;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.order) params.order = filters.order;

      const response = await axiosInstance.get('/api/admin/dashboard/app-credit-usage', {
        headers: { token },
        params,
      });

      const exportData = response.data.data.data;
      if (!exportData.length) { alert('No data to export'); return; }

      const headers = ['Username', 'Transaction ID', 'Amount', 'Message', 'Created At', 'Updated At'];
      const csvContent = [
        headers.join(','),
        ...exportData.map((row) => [
          row.username || '',
          row.tranx_id || '',
          row.amount ?? '',
          `"${(row.message || '').replace(/"/g, '""')}"`,
          row.createdAt ? formatDate(row.createdAt) : '',
          row.updatedAt ? formatDate(row.updatedAt) : '',
        ].join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', `credits_usage_history_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Please try again.');
    }
  };

  const thCls = "text-left";
  const tdCls = "border-b border-border/40 px-5 py-3.5 text-sm text-foreground";

  return (
    <div>
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Community
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        Credits Usage <span className="text-gradient-ginox">History</span>
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        View credit usage transactions across all services.
      </p>

      {/* Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Type
            </label>
            <select
              value={tempFilters.type}
              onChange={(e) => handleTempFilterChange('type', e.target.value)}
              className={inputCls}
            >
              <option value="">All Types</option>
              {CREDIT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Username / WalletAddress
            </label>
            <input
              type="text"
              value={tempFilters.username}
              onChange={(e) => handleTempFilterChange('username', e.target.value)}
              placeholder="Search…"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Order
            </label>
            <select
              value={tempFilters.order}
              onChange={(e) => handleTempFilterChange('order', e.target.value)}
              className={inputCls}
            >
              <option value="">Default</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Start Date
            </label>
            <input
              type="date"
              value={tempFilters.startDate}
              onChange={(e) => handleTempFilterChange('startDate', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              End Date
            </label>
            <input
              type="date"
              value={tempFilters.endDate}
              onChange={(e) => handleTempFilterChange('endDate', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Page Size
            </label>
            <select
              value={tempFilters.pageSize}
              onChange={(e) => handleTempFilterChange('pageSize', parseInt(e.target.value))}
              className={inputCls}
            >
              {[10, 15, 25, 50, 100].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={applyFilters}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
          >
            Apply Filters
          </button>
          <button
            onClick={clearFilters}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Clear Filters
          </button>
          <button
            onClick={exportToCSV}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
          >
            Export to CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <table className="w-full border-separate border-spacing-0">
            <tbody>
              <tr>
                <td className="py-12 text-center text-sm text-muted-foreground">Loading...</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <>
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <SortableTableHeader sortKey="username" currentSort={sort} onSort={handleSort} className={thCls}>
                    Username
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="tranx_id" currentSort={sort} onSort={handleSort} className={thCls}>
                    Transaction ID
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="amount" currentSort={sort} onSort={handleSort} className={thCls}>
                    Amount
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="message" currentSort={sort} onSort={handleSort} className={thCls}>
                    Message
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="createdAt" currentSort={sort} onSort={handleSort} className={thCls}>
                    Created At
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="updatedAt" currentSort={sort} onSort={handleSort} className={thCls}>
                    Updated At
                  </SortableTableHeader>
                </tr>
              </thead>
              <tbody>
                {sortedData.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-sm text-muted-foreground">
                      No data found
                    </td>
                  </tr>
                ) : (
                  sortedData.map((item) => (
                    <tr key={item._id} className="group hover:bg-white/[0.02] transition-colors">
                      <td className={tdCls}>{item.username}</td>
                      <td className={`${tdCls} font-mono text-xs`}>{item.tranx_id}</td>
                      <td className={`${tdCls} font-mono`}>{item.amount}</td>
                      <td className={`${tdCls} max-w-xs truncate`}>{item.message}</td>
                      <td className={`${tdCls} font-mono text-xs`}>{formatDate(item.createdAt)}</td>
                      <td className={`${tdCls} font-mono text-xs`}>{formatDate(item.updatedAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalRows={pagination.totalRows}
          pageSize={filters.pageSize}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default CreditsUsageHistory;
